import { createApiHooks, useGenericGet, useGenericPost } from '../../../../../hooks/use-api.hook';
import { apiClient } from '../../../../../services/api-client.service';
import type {
  VisaApplication,
  VisaApplicationCreatePayload,
  VisaApplicationUpdatePayload,
  VisaApplicationExportRequest,
  VisaApplicationExportResponse,
} from '../types/visa-application.types';
import type { UseListParams } from '../../../../../types/pagination.types';
import type { ApiResponse } from '../../../../../types/api-response.types';
import type { FilterItem } from '../../../../../types/filters.types';
import { useMemo } from 'react';
import { useActiveTenant } from '../../../../../states/active-tenant.atom';

export interface VisaApplicationGetResponse extends ApiResponse<VisaApplication> {
  data: VisaApplication;
}

export interface VisaApplicationGetFiltersResponse {
  data: FilterItem[];
}

const useVisaApplicationBasePath = () => {
  const { activeTenant } = useActiveTenant();
  return useMemo(() => {
    return `/v1/admin/tenants/${activeTenant?.id}/visa-applications`;
  }, [activeTenant?.id]);
};

export const useVisaApplicationHooks = () => {
  const basePath = useVisaApplicationBasePath();
  const { activeTenant } = useActiveTenant();

  return createApiHooks<
    VisaApplication,
    VisaApplicationGetResponse,
    VisaApplicationCreatePayload,
    VisaApplicationUpdatePayload
  >({
    basePath,
    queryKey: ['VisaApplications', 'Tenant', activeTenant!.id],
    apiClient,
  });
};

export const useVisaApplicationGetFilters = (queryOptions?: any) => {
  const basePath = useVisaApplicationBasePath();
  return useGenericGet<VisaApplicationGetFiltersResponse>(
    `${basePath}/filters`,
    ['VisaApplications', 'filters'],
    apiClient,
    queryOptions
  );
};

export const useVisaApplicationApi = () => {
  const hooks = useVisaApplicationHooks();

  return {
    useGet: hooks.useGet,
    useCreate: hooks.useCreate,
    useUpdate: hooks.useUpdate,
    useDelete: hooks.useDelete,
    useList: (params: UseListParams, queryOptions?: any) => hooks.useList(params, queryOptions),
  };
};

export const useVisaApplicationGet = (id: string | number, queryOptions?: any) => {
  const { useGet } = useVisaApplicationApi();
  return useGet(id, queryOptions);
};

export const useVisaApplicationCreate = (mutationOptions?: any) => {
  const { useCreate } = useVisaApplicationApi();
  return useCreate(mutationOptions);
};

export const useVisaApplicationUpdate = (mutationOptions?: any) => {
  const { useUpdate } = useVisaApplicationApi();
  return useUpdate(mutationOptions);
};

export const useVisaApplicationDelete = (mutationOptions?: any) => {
  const { useDelete } = useVisaApplicationApi();
  return useDelete(mutationOptions);
};

export const useVisaApplicationList = (params: UseListParams, queryOptions?: any) => {
  const { useList } = useVisaApplicationApi();
  return useList(params, queryOptions);
};

export const useVisaApplicationExport = (queryOptions?: any) => {
  const basePath = useVisaApplicationBasePath();
  return useGenericPost<VisaApplicationExportResponse, VisaApplicationExportRequest>(
    `${basePath}/export`,
    ['VisaApplications', 'export'],
    apiClient,
    queryOptions
  );
};
